import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { AuthBrandPanel } from "./LoginPage";

const inputClass =
  "h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,15}$/;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!PASSWORD_RE.test(password)) {
      return setError("Password must be 8–15 characters with letters, numbers, and a special character.");
    }
    if (password !== confirm) {
      return setError("Passwords do not match.");
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-bone pt-[72px] lg:grid-cols-2" data-testid="reset-password-page">
      <AuthBrandPanel />
      <div className="flex items-center justify-center px-4 py-16 sm:px-8">
        <div className="w-full max-w-md">
          {done ? (
            <div data-testid="reset-password-success">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold/15 text-gold-dark">
                <CheckCircle2 className="h-6 w-6" />
              </span>
              <h1 className="mt-5 font-display text-2xl font-extrabold text-forest-deep">Password updated</h1>
              <p className="mt-3 text-sm text-muted-foreground">You can now log in with your new password.</p>
              <Link to="/login" data-testid="reset-go-login-btn"
                className="mt-6 inline-block rounded-full bg-forest px-8 py-3.5 font-display text-sm font-bold text-bone">
                Go to login
              </Link>
            </div>
          ) : (
            <>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Account recovery</p>
              <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-forest-deep">
                Choose a new password
              </h1>
              {!token && (
                <p className="mt-3 rounded-xl bg-destructive/10 px-4 py-2.5 text-sm font-medium text-destructive">
                  This reset link is missing its token. Please request a new one.
                </p>
              )}
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-password-form">
                <input data-testid="reset-password-input" type="password" required autoComplete="new-password"
                  className={inputClass} placeholder="New password"
                  value={password} onChange={(e) => setPassword(e.target.value)} />
                <input data-testid="reset-confirm-input" type="password" required autoComplete="new-password"
                  className={inputClass} placeholder="Re-enter new password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                {error && (
                  <p data-testid="reset-error" className="rounded-xl bg-destructive/10 px-4 py-2.5 text-sm font-medium text-destructive">
                    {error}
                  </p>
                )}
                <button type="submit" data-testid="reset-submit-btn" disabled={loading || !token}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-forest font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Update password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
